// client/src/App.js

import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Loader } from 'lucide-react';
import PropTypes from 'prop-types';
import { PERMISSIONS, hasPermission } from './utils/permissions';
import { api, setupAuthToken, ApiError, performLogout, isTokenExpiring, refreshAuthToken } from './utils/apiClient';
import { theme } from './styles/app/theme';
import { NotificationProvider } from './services/NotificationContext';
import './App.css';

// Lazy load all components
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const MoldManagement = lazy(() => import('./components/app/MoldManagement'));
const Login = lazy(() => import('./components/app/Login'));
const Dashboard = lazy(() => import('./components/app/Dashboard'));
const Navbar = lazy(() => import('./components/app/Navbar'));
const Profile = lazy(() => import('./components/app/Profile'));
const UserManagement = lazy(() => import('./components/app/UserManagement'));
const MachineManagement = lazy(() => import('./components/app/MachineManagement'));
const PartManagement = lazy(() => import('./components/app/PartManagement'));
const AIChat = lazy(() => import('./components/app/AIChat'));
const WorkOrder = lazy(() => import('./components/app/WorkOrder'));
const KaizenForm = lazy(() => import('./components/app/KaizenManagement/KaizenForm'));
const KaizenList = lazy(() => import('./components/app/KaizenManagement/KaizenList'));
const KaizenLeaderboard = lazy(() => import('./components/app/KaizenManagement/KaizenLeaderboard'));
const Prompter = lazy(() => import('./components/app/Prompter/Prompter'));
const AdminPanel = lazy(() => import('./components/app/AdminPanel'));
const MoldScheduler = lazy(() => import('./components/app/MoldScheduler'));
const AreaList = lazy(() => import('./components/app/SixS/AreaList'));
const AuditForm = lazy(() => import('./components/app/SixS/AuditForm'));
const Analysis = lazy(() => import('./components/app/SixS/Analysis'));
const SixSManagement = lazy(() => import('./components/app/SixS/SixSManagement'));
const AnalyticsDashboard = lazy(() => import('./components/app/AnalyticsDashboard'));
const ShiftReport = lazy(() => import('./components/app/ShiftReport'));
const SafetyAreaList = lazy(() => import('./components/app/Safety/AreaList'));
const SafetyInspectionForm = lazy(() => import('./components/app/Safety/InspectionForm'));
const SafetyAuditForm = lazy(() => import('./components/app/Safety/AuditForm'));
const SafetyAnalysis = lazy(() => import('./components/app/Safety/Analysis'));
const SafetyManagement = lazy(() => import('./components/app/Safety/SafetyManagement'));
const SafetyIncidentList = lazy(() => import('./components/app/Safety/SafetyIncidentList'));
const SafetyAuditList = lazy(() => import('./components/app/Safety/SafetyAuditList'));
const SafetyIncidentDetails = lazy(() => import('./components/app/Safety/SafetyIncidentDetails'));
const SafetyAuditDetails = lazy(() => import('./components/app/Safety/SafetyAuditDetails'));
const PMDashboard = lazy(() => import('./components/app/PreventiveMaintenance/PMDashboard'));
const PMAreaList = lazy(() => import('./components/app/PreventiveMaintenance/PMAreaList'));
const PMAreaForm = lazy(() => import('./components/app/PreventiveMaintenance/PMAreaForm'));
const PMTaskExecution = lazy(() => import('./components/app/PreventiveMaintenance/PMTaskExecution'));
const PMAnalytics = lazy(() => import('./components/app/PreventiveMaintenance/PMAnalytics'));

// Wrapper components for routes with params
const PMAreaEditWrapper = () => {
  const { id } = useParams();
  return <PMAreaForm areaId={id} />;
};

const PMTaskExecutionWrapper = () => {
  const { areaId } = useParams();
  return <PMTaskExecution areaId={areaId} />;
};

// Create a layout component for authenticated pages
const AuthenticatedLayout = ({ user, setUser, handleLogout, children }) => {
  return (
    <>
      <Suspense fallback={<LoadingSpinner />}>
        <Navbar user={user} setUser={setUser} logout={handleLogout} />
      </Suspense>
      {children}
      <Suspense fallback={<LoadingSpinner />}>
        <Prompter />
      </Suspense>
    </>
  );
};

AuthenticatedLayout.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
  }),
  setUser: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-600 p-4">
            <h1>Something went wrong.</h1>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const LoadingSpinner = () => (
  <output 
    className="flex items-center justify-center min-h-screen"
    aria-busy="true"
    aria-live="polite"
  >
    <Loader className="animate-spin w-8 h-8 text-blue-500" aria-hidden="true" />
    <span className="sr-only">Loading content...</span>
  </output>
);

const ProtectedRoute = ({ user, children, permission }) => {
  if (!user) {
    return <Navigate to="/login" />;
  }

  if (permission && !hasPermission(user?.role, permission)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  user: PropTypes.shape({
    role: PropTypes.string.isRequired,
  }),
  children: PropTypes.node.isRequired,
  permission: PropTypes.string,
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const handleLogout = () => {
    performLogout(setUser);
  };

  const fetchUser = useCallback(async (skipLoadingState = false) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    if (!skipLoadingState) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await api.auth.me();
      if (response?.data?.user) {
        setUser(response.data.user);
      } else {
        throw new ApiError('Invalid user data received', 401);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      // Only clear user state on auth errors
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
      setError(err instanceof ApiError ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let sessionCheckInterval;

    const initializeApp = async () => {
      if (!mounted) return;

      try {
        const token = localStorage.getItem('token');
        if (token) {
          setupAuthToken();
          const payload = JSON.parse(atob(token.split('.')[1]));
          
          if (payload.exp * 1000 <= Date.now()) {
            // Token is expired, try to refresh
            try {
              await refreshAuthToken();
              await fetchUser(true); // Skip setting loading state again
            } catch (error) {
              if (error instanceof ApiError && error.status === 401) {
                setUser(null);
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
              }
            }
          } else {
            // Token is valid, fetch user
            await fetchUser(true);
          }
        }
      } catch (error) {
        console.error('Initialization error:', error);
        if (mounted) {
          setError('Failed to initialize application');
        }
      } finally {
        if (mounted) {
          setInitialized(true);
          setLoading(false);
        }
      }
    };

    initializeApp();

    // Set up periodic token refresh check
    sessionCheckInterval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        if (isTokenExpiring(token)) {
          await refreshAuthToken();
          // Refresh user data after token refresh
          await fetchUser(true);
        }
      } catch (error) {
        console.error('Token refresh check failed:', error);
        if (error instanceof ApiError && error.status === 401) {
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }
    }, 60000); // Check every minute

    return () => {
      mounted = false;
      clearInterval(sessionCheckInterval);
    };
  }, [fetchUser]);

  if (!initialized) {
    return (
      <ThemeProvider theme={theme}>
        <LoadingSpinner />
      </ThemeProvider>
    );
  }

  // Create a wrapper for protected routes that includes the authenticated layout
  const ProtectedRouteWithLayout = ({ children, permission }) => (
    <ProtectedRoute user={user} permission={permission}>
      <AuthenticatedLayout user={user} setUser={setUser} handleLogout={handleLogout}>
        {children}
      </AuthenticatedLayout>
    </ProtectedRoute>
  );

  ProtectedRouteWithLayout.propTypes = {
    children: PropTypes.node.isRequired,
    permission: PropTypes.string,
  };

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <NotificationProvider>
            <Router>
              <div className="App">
                <Helmet defaultTitle="KyckTech Dashboard">
                  <meta name="description" content="KyckTech Manufacturing Intelligence Platform" />
                  <meta name="theme-color" content="#3B82F6" />
                </Helmet>

                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={
                      user ? <Navigate to="/dashboard" /> : <LandingPage />
                    } />

                    <Route path="/login" element={
                      !user ? (
                        <>
                          <Helmet>
                            <title>Login - KyckTech</title>
                          </Helmet>
                          <Login setUser={setUser} />
                        </>
                      ) : (
                        <Navigate to="/dashboard" />
                      )
                    } />

                    {/* Protected routes - all using the authenticated layout */}
                    <Route path="/dashboard" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_DASHBOARD}>
                        <Helmet>
                          <title>Dashboard - KyckTech</title>
                        </Helmet>
                        <Dashboard user={user} setUser={setUser} />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/work-orders" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_WORK_ORDERS}>
                        <Helmet>
                          <title>Work Orders - KyckTech</title>
                        </Helmet>
                        <WorkOrder />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/profile" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_DASHBOARD}>
                        <Helmet>
                          <title>Profile - KyckTech</title>
                        </Helmet>
                        <Profile user={user} />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/user-management" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_USERS}>
                        <Helmet>
                          <title>User Management - KyckTech</title>
                        </Helmet>
                        <UserManagement />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/machine-management" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_MACHINES}>
                        <Helmet>
                          <title>Machine Management - KyckTech</title>
                        </Helmet>
                        <MachineManagement />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/ai-chat" element={
                      <ProtectedRouteWithLayout>
                        <Helmet>
                          <title>AI Chat - KyckTech</title>
                        </Helmet>
                        <AIChat user={user} />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/mold-management" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_MOLDS}>
                        <Helmet>
                          <title>Mold Management - KyckTech</title>
                        </Helmet>
                        <MoldManagement />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/part-management" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_PARTS}>
                        <Helmet>
                          <title>Part Management - KyckTech</title>
                        </Helmet>
                        <PartManagement />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/analytics" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_ANALYTICS}>
                        <Helmet>
                          <title>Analytics Dashboard - KyckTech</title>
                        </Helmet>
                        <AnalyticsDashboard />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/shift-report" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_ANALYTICS}>
                        <Helmet>
                          <title>Shift Report - KyckTech</title>
                        </Helmet>
                        <ShiftReport />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/areas" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_SAFETY_AREAS}>
                        <Helmet>
                          <title>Safety Areas - KyckTech</title>
                        </Helmet>
                        <SafetyAreaList />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/inspection/:areaId" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.CONDUCT_SAFETY_INSPECTION}>
                        <Helmet>
                          <title>Safety Inspection - KyckTech</title>
                        </Helmet>
                        <SafetyInspectionForm />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/audit/:areaId" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.CONDUCT_SAFETY_AUDIT}>
                        <Helmet>
                          <title>Safety Audit - KyckTech</title>
                        </Helmet>
                        <SafetyAuditForm />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/analysis" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_SAFETY_ANALYTICS}>
                        <Helmet>
                          <title>Safety Analysis - KyckTech</title>
                        </Helmet>
                        <SafetyAnalysis />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/incidents" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_SAFETY_AREAS}>
                        <Helmet>
                          <title>Safety Incidents - KyckTech</title>
                        </Helmet>
                        <SafetyIncidentList />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/incidents/:id" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_SAFETY_INCIDENTS}>
                        <Helmet>
                          <title>Incident Details - KyckTech</title>
                        </Helmet>
                        <SafetyIncidentDetails />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/audits" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_SAFETY_AREAS}>
                        <Helmet>
                          <title>Safety Audits - KyckTech</title>
                        </Helmet>
                        <SafetyAuditList />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/audits/:id/view" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.CONDUCT_SAFETY_AUDIT}>
                        <Helmet>
                          <title>Audit Details - KyckTech</title>
                        </Helmet>
                        <SafetyAuditDetails />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/safety/management" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_SAFETY_AREAS}>
                        <Helmet>
                          <title>Safety Management - KyckTech</title>
                        </Helmet>
                        <SafetyManagement />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/sixs/areas" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_SIXS}>
                        <Helmet>
                          <title>6S Areas - KyckTech</title>
                        </Helmet>
                        <AreaList />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/sixs/audit/:areaId" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.CONDUCT_SIXS_AUDIT}>
                        <Helmet>
                          <title>6S Audit - KyckTech</title>
                        </Helmet>
                        <AuditForm />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/sixs/analysis" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_SIXS_ANALYSIS}>
                        <Helmet>
                          <title>6S Analysis - KyckTech</title>
                        </Helmet>
                        <Analysis />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/sixs/manage" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_SIXS_AREAS}>
                        <Helmet>
                          <title>6S Area Management - KyckTech</title>
                        </Helmet>
                        <SixSManagement />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/kaizen/create" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.CREATE_KAIZEN}>
                        <Helmet>
                          <title>Create Kaizen - KyckTech</title>
                        </Helmet>
                        <KaizenForm />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/kaizen" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_KAIZEN}>
                        <Helmet>
                          <title>Kaizen List - KyckTech</title>
                        </Helmet>
                        <KaizenList user={user} />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/kaizen/leaderboard" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_KAIZEN}>
                        <Helmet>
                          <title>Kaizen Leaderboard - KyckTech</title>
                        </Helmet>
                        <KaizenLeaderboard />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/admin-panel" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_USERS}>
                        <Helmet>
                          <title>Admin Panel - KyckTech</title>
                        </Helmet>
                        <AdminPanel />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/mold-schedule" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_WORK_ORDERS}>
                        <Helmet>
                          <title>Mold Schedule - KyckTech</title>
                        </Helmet>
                        <MoldScheduler />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/pm/dashboard" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_PM_AREAS}>
                        <Helmet>
                          <title>PM Dashboard - KyckTech</title>
                        </Helmet>
                        <PMDashboard />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/pm/areas" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_PM_AREAS}>
                        <Helmet>
                          <title>PM Areas - KyckTech</title>
                        </Helmet>
                        <PMAreaList />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/pm/areas/new" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_PM_AREAS}>
                        <Helmet>
                          <title>Create PM Area - KyckTech</title>
                        </Helmet>
                        <PMAreaForm />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/pm/areas/:id/edit" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.MANAGE_PM_AREAS}>
                        <Helmet>
                          <title>Edit PM Area - KyckTech</title>
                        </Helmet>
                        <PMAreaEditWrapper />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/pm/tasks/:areaId" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.EXECUTE_PM_TASKS}>
                        <Helmet>
                          <title>PM Task Execution - KyckTech</title>
                        </Helmet>
                        <PMTaskExecutionWrapper />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="/pm/analytics" element={
                      <ProtectedRouteWithLayout permission={PERMISSIONS.VIEW_PM_ANALYTICS}>
                        <Helmet>
                          <title>PM Analytics - KyckTech</title>
                        </Helmet>
                        <PMAnalytics />
                      </ProtectedRouteWithLayout>
                    } />

                    <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
                  </Routes>
                </Suspense>
              </div>
            </Router>
          </NotificationProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default App;